import _ from 'lodash';
import moment from 'moment';
import constants from '$$helpers/constants';
import estimationsModule from '../../../components/estimations';
import projectsModel from '$$projectModel';
import Store from '$$globalStore';
import customDayTemplatesModel from '$$customDayTemplatesModel';
import timeTrackingModel from '$$timeTrackingModel';
import chartsConfig from './chartsConfig';
import app from '$$app';
import reportHelpers from '../include/helpers';
import globalToast from '$$vueCmp/toast';

class ProjectsBudgetReport {
  constructor() {
    this.costWorker = null;
    this.reportTasks = {};
    this.calculationMode = null;
  }

  initCostWorker() {
    if (window.Worker && !this.costWorker) {
      let version = Date.now();

      if (GT.productVersion && GT.productVersion.buildVersion) {
        version = GT.productVersion.buildVersion;
      }

      this.costWorker = new Worker('/workers/costs_worker.js' + `?${version}`);
    }

    this._initEvents();
  }

  _initEvents() {
    const that = this;

    this.costWorker.onmessage = function (e) {
      const updatedCosts = e.data;

      Object.entries(updatedCosts).forEach(([key, updates]) => {
        if (updates.length) {
          const tasks = that.reportTasks[key]?.tasks;

          if (Array.isArray(tasks)) {
            that.reportTasks[key].tasks = tasks.map(task => {
              const update = updates.find(obj => obj.id === task.id);

              if (update) {
                return {
                  ...task,
                  cost: update.cost,
                  actual_cost: update.actual_cost,
                };
              }

              return task;
            });
          }
        }
      });

      that.calculateProjectsBudgetReport();
    };

    this.costWorker.onerror = function (e) {
      globalToast.error(__('costs_calculating_error'));
    };
  }

  calculateCosts(projectIds, baselineTasks, calculationMode) {
    this.calculationMode = calculationMode;
    this.reportTasks = {};

    const currentFilter = Store.getters['filter/currentFilter'];

    if (projectIds?.length) {
      projectIds.forEach(id => {
        const allTasks = Store.getters['tasksModel/getTasksForGantt'](id).data;

        const totalEstimate = allTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
        const projectTasks = allTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

        const preapredTasks = this.prepareTasksForWorker(projectTasks);
        const filteredTasks = this.returnTasksByCurrentFilter(preapredTasks, currentFilter);
        const range = {
          start: currentFilter?.properties?.rangeDate?.start || totalEstimate.start_date,
          end: currentFilter?.properties?.rangeDate?.end || totalEstimate.end_date,
        };

        this.reportTasks[id] = { tasks: filteredTasks, range };
      });
    }

    const baselineTotalEstimate = baselineTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
    const baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
    const preapredBaselineTasks = this.prepareTasksForWorker(baselineTasksAndMilestones);
    const filteredBaselineTasks = this.returnTasksByCurrentFilter(preapredBaselineTasks, currentFilter);
    const baselineRange = {
      start: currentFilter?.properties?.rangeDate?.start || baselineTotalEstimate?.start_date || moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: currentFilter?.properties?.rangeDate?.end || baselineTotalEstimate?.end_date || moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    };

    this.reportTasks = {
      ...this.reportTasks,
      baseline: { tasks: filteredBaselineTasks, range: baselineRange },
    };

    this.sendDataToWorker(this.reportTasks);
  }

  sendDataToWorker(tasksData) {
    const projectSettingsMap = {};

    projectsModel.serialize().forEach(settings => {
      projectSettingsMap[settings.gantt_id] = {
        ...settings,
        totalEstimate: Store.getters['tasksModel/getTotalEstimateDataForProject'](settings.gantt_id),
      };
    });

    this.costWorker.postMessage(_.cloneDeep({
      projectConfigs: reportHelpers.prepareProjectConfigs(),
      projectSettings: projectSettingsMap,
      customDaysData: customDayTemplatesModel.serialize(),
      tasksData,
      resourcesData: Store.getters['resourcesModel/getAllResources'],
    }));
  }

  returnTasksByCurrentFilter(tasks, currentFilter) {
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const filterRange = startDate && endDate ? { start: startDate, end: endDate } : null;

    return tasks.filter(task => reportHelpers.returnTaskByRange({
      ...task,
      start_date: task.task_data.start_date,
      end_date: task.task_data.end_date,
    }, filterRange)).filter(task => {
      if (!currentFilter?.properties?.assignee?.length) {
        return task;
      }

      if (currentFilter.properties.assignee.includes('-1') && !task.resource_id.length) {
        return task;
      }

      const assigneeTask = task.resource_id.find(id => currentFilter.properties.assignee.includes(+id));

      if (assigneeTask) {
        return task;
      }
    }).filter(task => {
      if (!currentFilter?.properties?.text?.length) {
        return task;
      }

      return task.task_data.text.toLowerCase().startsWith(currentFilter.properties.text.toLowerCase());
    });
  }

  prepareTasksForWorker(tasks) {
    const resultData = [];

    _.each(tasks, taskData => {
      const projectData = projectsModel.getProjectDataById(taskData.gantt_id);
      const resources = Store.getters['resourcesModel/getResourcesByTaskIdAndGanttId'](taskData.gantt_id, taskData.id);
      const timeLogsData = timeTrackingModel.getLogsByParams(taskData.gantt_id, taskData.id);

      resultData.push({
        id: taskData.id,
        gantt_id: taskData.gantt_id,
        task_id: taskData.id, // do not remove
        project_name: projectData.name,
        task_name: taskData.text,
        task_data: taskData,
        info_text: taskData.note,
        resources,
        resource_id: _.map(resources, res => String(res.id)),
        resource_data: resources,
        time_logs: timeLogsData ? timeLogsData.sum : '0',
        cost: estimationsModule.calculateCostChildTask(taskData) || 0,
        actual_cost: estimationsModule.calculateActualCostChildTask(taskData) || 0,
        cost_range_start: taskData.start_date,
        cost_range_end: taskData.end_date,
        logsData: timeTrackingModel.getLogsByParams(taskData.gantt_id, taskData.id),
      });
    });

    return resultData;
  }

  calculateProjectsBudgetReport() {
    let actualCost;
    let plannedCost;
    let baselinePlannedCost;
    let variance;

    let resultCount = 0;

    const resultConfig = chartsConfig.projectsReport(this.calculationMode).default;
    const baselineTasks = this.reportTasks.baseline?.tasks;
    const projectsTasks = _.omit(this.reportTasks, ['baseline']);

    Object.entries(projectsTasks).forEach(([projectId, data]) => {
      actualCost = 0;
      plannedCost = 0;
      baselinePlannedCost = 0;
      variance = 0;

      const projectData = projectsModel.getProjectDataById(projectId);

      actualCost = data.tasks.reduce((sum, task) => sum + task.actual_cost, 0);

      if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED) {
        plannedCost = data.tasks.reduce((sum, task) => sum + task.cost, 0);

        if (actualCost === 0 && plannedCost === 0) {
          return;
        }

        variance = plannedCost - actualCost;
      }

      if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
        baselinePlannedCost = baselineTasks.reduce((sum, task) => sum + task.cost, 0);

        if (actualCost === 0 && baselinePlannedCost === 0) {
          return;
        }

        variance = baselinePlannedCost - actualCost;
      }

      if (this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
        plannedCost = data.tasks.reduce((sum, task) => sum + task.cost, 0);
        baselinePlannedCost = baselineTasks.reduce((sum, task) => sum + task.cost, 0);

        if (baselinePlannedCost === 0 && plannedCost === 0) {
          return;
        }

        variance = baselinePlannedCost - plannedCost;
      }

      resultConfig.xAxis.data.push(projectData.name);
      resultConfig.series = resultConfig.series.map(item => {
        const currentCostForGraphicValue1 = this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE
          ? plannedCost
          : actualCost;

        const nameForTooltipLabel1 = this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE
          ? 'planned'
          : 'actual';

        let currentCostForGraphicValue2 = plannedCost;
        let nameForTooltipLabel2 = 'planned'

        if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE || this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
          currentCostForGraphicValue2 = baselinePlannedCost;
          nameForTooltipLabel2 = 'baseline'
        }

        if (item.name === 'cost_data_1') {
          let labelPosition = 'insideTop';

          if (currentCostForGraphicValue1 < 0) {
            labelPosition = 'insideBottom';
          }

          if (currentCostForGraphicValue1 === 0) {
            labelPosition = 'top';
          }

          return {
            ...item,
            data: [
              ...item.data,
              {
                value: currentCostForGraphicValue1.toFixed(2),
                label: {
                  name: nameForTooltipLabel1,
                  position: labelPosition,
                  color: currentCostForGraphicValue1 === 0 ? '#191919' : '#fff',
                },
              },
            ],
          };
        }

        if (item.name === 'cost_data_2') {
          let labelPosition = 'insideTop';

          if (currentCostForGraphicValue2 < 0) {
            labelPosition = 'insideBottom';
          }

          if (currentCostForGraphicValue2 === 0) {
            labelPosition = 'top';
          }

          return {
            ...item,
            data: [
              ...item.data,
              {
                value: currentCostForGraphicValue2.toFixed(2),
                label: {
                  name: nameForTooltipLabel2,
                  position: labelPosition,
                  color: currentCostForGraphicValue2 === 0 ? '#191919' : '#fff',
                },
              },
            ],
          };
        }

        if (item.name === 'variance') {
          let labelPosition = 'insideTop';

          if (variance < 0) {
            labelPosition = 'insideBottom';
          }

          if (variance === 0) {
            labelPosition = 'top';
          }

          return {
            ...item,
            data: [
              ...item.data,
              {
                value: variance.toFixed(2),
                label: {
                  name: 'variance',
                  position: labelPosition,
                  color: variance === 0 ? '#191919' : '#fff',
                },
              },
            ],
          };
        }
      });

      resultCount++;
    });

    if (resultCount > 0) {
      app.trigger('onAfterProjectsBudgetCalculation', resultConfig);

      return;
    }

    app.trigger('onAfterProjectsBudgetCalculation', null);
  }
}

const projectsBudgetReport = new ProjectsBudgetReport();

export default projectsBudgetReport;
